import { MutationType, SubChartType } from '@tencent/slide-sdk-pb-model';
import { assert } from 'ts-essentials';
import { BaseMutation } from '../../base';
import { DataType } from '../../../data';
import { Rule } from '../../base/mutation-rules';
import { SubChart } from './sub-chart';
import type { IPlotChartData } from './sub-chart';
import type { IValidSetSubChartPropertiesMutationData } from '../../base';
import type { IEGPlotDelta } from '@tencent/slide-sdk-pb-model';
import type { ISubChartData } from '../../../data/chart/chart-data';
import type { PresentationDataModel } from '../../../model/presentation-data-model';
import type { MaybeError } from '../../base/base-mutation';

// omit掉chartType的原因是，这个chartType是更细粒度的描述，有些与当前存在的图表不能很好的映射如股价图
// 所以整体数据还是以egPlot中携带的数据为什么图表的key来做关联
export type IEgPlotProps = Omit<IEGPlotDelta, 'chartType'>;
export const egPlotSubChartTypeMapping: Record<keyof IEgPlotProps, SubChartType> = {
    areaChart: SubChartType.AreaChart,
    area3DChart: SubChartType.Area3DChart,
    lineChart: SubChartType.LineChart,
    line3DChart: SubChartType.Line3DChart,
    stockChart: SubChartType.StockChart,
    radarChart: SubChartType.RadarChart,
    scatterChart: SubChartType.ScatterChart,
    pieChart: SubChartType.PieChart,
    pie3DChart: SubChartType.Pie3DChart,
    doughnutChart: SubChartType.DoughnutChart,
    barChart: SubChartType.BarChart,
    bar3DChart: SubChartType.Bar3DChart,
    ofPieChart: SubChartType.OfPieChart,
    surfaceChart: SubChartType.SurfaceChart,
    surface3DChart: SubChartType.Surface3DChart,
    bubbleChart: SubChartType.BubbleChart,
};
export interface IPlotSubChart {
    props: IPlotChartData;
    type: SubChartType;
}

export class SetSubChartMutation extends BaseMutation<PresentationDataModel, IValidSetSubChartPropertiesMutationData> {
    public readonly type: MutationType = MutationType.SetSubChartProperties;

    public apply(presentation: PresentationDataModel) {
        const { shapeId, index } = this.data;
        const chart = presentation.store.models.get(shapeId, DataType.GraphicalObjectFrameChart);
        assert(chart, 'chart should exist');

        chart.store.setSubChart(this.normalizeSubChartData(), index);
    }

    public check(presentation: PresentationDataModel): MaybeError {
        const { pageId, shapeId, index, plot } = this.data;
        const chart = presentation.store.models.get(shapeId, DataType.GraphicalObjectFrameChart);
        assert(chart, 'chart should exist');

        const subChart = this.getSubChartInEgPlot(plot);

        return Rule.every(
            Rule.shape.should.existInPage(presentation, this.data.shapeId, this.data.pageId),
            Rule.chart.should.subChartExist(presentation, pageId, shapeId, index),
            Rule.chart.is.matchedSubChartType(presentation, pageId, shapeId, index, subChart?.type),
        );
    }

    public invert(presentation: PresentationDataModel): [IValidSetSubChartPropertiesMutationData] {
        const { pageId, shapeId, index } = this.data;

        const chart = presentation.store.models.get(shapeId, DataType.GraphicalObjectFrameChart);
        assert(chart, 'chart should exist');
        const subChart = chart.store.subChartList[index];
        assert(subChart, 'subChart should exist');

        return [
            {
                type: MutationType.SetSubChartProperties,
                pageId,
                shapeId,
                index,
                // 图表的子表是全量替换，所以不需要增量invert
                plot: new SubChart().createEgPlotBySubChart(chart.store.subChartList[index]),
                axesPr: {
                    axes: subChart.axes,
                    index: subChart.axesAddIndex,
                },
            },
        ];
    }

    private normalizeSubChartData(): ISubChartData {
        const { plot, axesPr } = this.data;

        const { type, props: subChart } = this.getSubChartInEgPlot(plot) || {};
        assert(subChart);

        const { axes, index } = axesPr || {};

        return new SubChart().toSdkSubChart(type as SubChartType, subChart, index, axes);
    }

    private getSubChartInEgPlot(plot: IEGPlotDelta): IPlotSubChart | undefined {
        let subChartInPlot: IPlotSubChart | undefined = undefined;

        Object.keys(plot).some((subChartKey) => {
            const mappedSubChartType = egPlotSubChartTypeMapping[subChartKey as keyof IEgPlotProps];
            if (mappedSubChartType) {
                subChartInPlot = {
                    // TODO 这里其实把 IEGPlotDelta 强转成了 ISubChartData，这里看有没有更好的方式
                    props: plot[subChartKey as keyof IEgPlotProps],
                    type: mappedSubChartType,
                };
                return true;
            }

            return false;
        });

        return subChartInPlot;
    }
}
